/**
 * Environment configuration for connection manager
 * Reads from environment variables and provides defaults
 */

export const DEFAULT_CONNECTION_FROM = "Upgrade-ws";
export const REST_CONNECTION_FROM = "rest";

export const SOURCE = {
  WEB: "WEB",
  APP: "APP",
};

/**
 * Get connection manager environment from Next.js env variable
 * Maps to connection-manager expected values
 */
export const CONNECTION_MANAGER_ENV = (() => {
  const env = process.env.NEXT_PUBLIC_CONNECTION_MANAGER_ENV?.toLowerCase();
  const envMapping: Record<string, string> = {
    uat: "UAT",
    iuat: "iUAT",
    production: "PRODUCTION",
    cont: "CONT",
  };
  return envMapping[env || ""] || "PRODUCTION";
})();

/**
 * API response status codes
 */
export const API_STATUS = {
  SUCCESS: "S",
  ERROR: "E",
} as const;

/**
 * API response codes for specific scenarios
 */
export const API_RESPONSE_CODES = {
  STATUS_CODE_7001: "7001",
  STATUS_CODE_7002: "7002",
  STATUS_CODE_7003: "7003",
  STATUS_CODE_7004: "7004",
  STATUS_CODE_7005: "7005",
  STATUS_CODE_5005: 5005, // Decryption error - requires retry
  STATUS_CODE_200: 200,
} as const;

/**
 * Maximum retry attempts for failed requests
 */
export const MAX_RETRIES = 1;

/**
 * Source identifier for ICORS APIs
 */
export const SOURCE_ID = "3";

/**
 * Web source identifiers
 */
export const WEB_SOURCE_IDENTIFIER = "3IN1-Web";
export const WEB_SOURCE_IDENTIFIER_GL = "2";

/**
 * Default values for loan operations
 */
export const B2C_DEFAULT_DOB = "1990-01-01";
export const PRODUCTS_APP_VERSION = "v2";
export const DEFAULT_OFFER_PRODUCT = "SPL";
export const DEFAULT_PROGRAM_NAME = "FULL UW";
export const DEFAULT_SEGMENT = "ALL";
export const DEFAULT_SOURCING_BRANCH_ID = "320";
export const DEFAULT_PROCESS_SEGMENTATION_C = "null";
export const DEFAULT_PROCESS_SEGMENTATION = "NEW";
export const DEFAULT_PROCESS_TYPE = "Full Underwriting";
export const DEFAULT_LOAN_TYPE = "New Loan";
export const DEFAULT_CURRENT_RESIDENCE_STATE = "ALL";

/**
 * Loan type mappings
 */
export const LOAN_TYPES_MAPPING: Record<string, string> = {
  term: "Normal Term Loan",
  flexi: "Dropline Flexi",
  hybrid: "Hybrid Flexi",
  "flexi-loan": "Dropline Flexi",
  emi_lite: "Term Loan Principal Holiday",
} as const;

/**
 * Event codes for various operations
 */
export const EVENT_CODE = {
  GOLD_LOAN: "GL_PDP",
  CD_LOAN: "PDP_DETAILS",
  QR_PDP_INSERT: "QR_PDP_INSERT",
  BLSE_SME_EVENT_CODE: "SME_BLSE",
} as const;

/**
 * Product names and codes
 */
export const PSBL_PROD_NAME = "PSBL";
export const PSBL_EVENT_CODE_V4 = "SME_PSBL";
export const BLSE_PROD_NAME = "BLSE";
export const BLSE_SME_EVENT_CODE = "SME_BLSE";
export const SME_EVENT_NAME = "PDP_Details";
export const CP_PL_STAMPING_UTM_SOURCE = "organic_bfl";
export const CP_PL_STAMPING_UTM_MEDIUM = "bfl";
export const APP_TYPE_PRIMARY = "Primary";
export const HYBRID = "hybrid";
export const PLCS_LOAN_VARIANT = "Term Loan";
export const SAL_NO_OFFER = "SAL_NO_OFFER";
export const PLCS_GIN = "PLCS_GIN";
export const HL_INSERT_EVENT_CODE = "HL_PDP";

/**
 * Application types
 */
export const APP_TYPE = {
  PRIMARY: "Primary",
} as const;

/**
 * Customer types
 */
export const BLSE_CUST_TYPE = {
  INDIVIDUAL: "Individual",
} as const;

/**
 * Error constants
 */
export const BFL_WHEELES_ERROR = "BFL_WHEELES_ERROR";

/**
 * Brand and version constants
 */
export const BRAND = "Brand";
export const RURAL = "rural";
export const V1 = "v1";
export const V3 = "v3";
export const V4 = "v4";
export const V5 = "v5";

/**
 * QR Offer types
 */
export const QR_OFFER_TYPES = [
  "Vouchers",
  "Voucher",
  "BFL Vouchers",
  "OEM Vouchers",
  "Customer NPV1",
  "Customer NPV2",
  "Cashback",
  "NPV1",
  "NPV2",
  "BajajCoins",
  "Bajaj Coins",
  "Brand Vouchers",
] as const;
